<template>
  <div class="incentive-cap-summary">
    <vs-button v-if="canSetCap" color="secondary" @click="openPopup">연도별 인센티브 Cap</vs-button>

    <vs-popup title="연도별 인센티브 Cap" :active.sync="showPopup">
      <div class="cap-popup">
        <div class="cap-popup-year">
          <span class="cap-popup-label">연도</span>
          <vs-select v-model="year" style="width: 120px" @input="loadCap">
            <vs-select-item v-for="y in yearItems" :key="`cap-year-${y}`" :text="`${y}년`" :value="y" />
          </vs-select>
          <span class="cap-popup-desc">* 사업장별 Safety Card를 줄 수 있는 한도(Cap) 점수를 입력합니다. Cap이 없는 사업장은 Safety Card 상신·승인이 진행되지 않습니다.</span>
        </div>

        <div v-if="loading" class="cap-popup-loading">
          <vs-progress indeterminate color="primary" />
        </div>
        <table v-else class="cap-popup-table">
          <colgroup>
            <col />
            <col style="width: 120px" />
            <col style="width: 100px" />
            <col style="width: 100px" />
          </colgroup>
          <thead>
            <tr>
              <th>사업장명</th>
              <th>Cap 점수(점)</th>
              <th>현재 발부(점)</th>
              <th>잔여(점)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in capList" :key="`cap-${rowIndex}`">
              <td>{{ row.사업장명 }}</td>
              <td>
                <ml-input type="number" v-model="row.점수" />
              </td>
              <td class="text-center">{{ issuedOf(row) }}</td>
              <td class="text-center" :class="remainClass(row)">{{ remainOf(row) }}</td>
            </tr>
            <tr v-if="capList.length === 0">
              <td colspan="4" class="text-center">사업장 정보가 없습니다.</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!loading" class="cap-popup-note">* 현재 발부 = {{ year }}년 발급일자 기준 결재완료(승인)된 Safety Card 점수 합계</div>

        <div class="cap-popup-buttons">
          <vs-button :disabled="loading || saving" @click="saveCap">저장</vs-button>
          <vs-button color="dark" type="border" @click="showPopup = false">닫기</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { CAP_BOARD_ID, loadIssuedScoreByField } from "./capUtils";

export default {
  props: {
    boardId: [Number, String],
    nav: Object,
    projectId: [Number, String],
    refreshKey: Number,
  },
  data() {
    return {
      showPopup: false,
      loading: false,
      saving: false,
      year: new Date().getFullYear(),
      capList: [],
      capPost: null,
      fieldNames: [],
      // 연도별 사업장 현재 발부(승인완료) 점수 { 사업장명: 점수 }
      issuedByField: {},
    };
  },
  computed: {
    memberInfo() {
      return this.$store.state.member.memberInfo || {};
    },
    isAdmin() {
      return parseInt(this.memberInfo.is_superuser) > 0;
    },
    // 연도별 인센티브 Cap 설정 권한: company_id 914~917 (및 관리자) - 2026-09 고객 확정
    // 914: 본사 / 915: 각 공장 및 연구소(안전환경팀) / 916: 관리감독자 / 917: 본사, 각 공장 및 연구소(일반직원)
    canSetCap() {
      const companyId = parseInt(this.$store.state.auth.userInfo.company_id);
      return this.isAdmin || [914, 915, 916, 917].includes(companyId);
    },
    yearItems() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current + 1; y >= current - 5; y--) {
        years.push(y);
      }
      return years;
    },
  },
  async created() {
    // 관리자(is_superuser) 판별용 회원 상세정보 로드
    if (!this.memberInfo.id) {
      try {
        await this.$store.dispatch("member/LOAD_MEMBER_DETAIL");
      } catch (e) {
        /* ignore */
      }
    }
  },
  methods: {
    async openPopup() {
      this.showPopup = true;
      await this.loadFieldNames();
      await this.loadCap();
    },
    async loadFieldNames() {
      if (this.fieldNames.length > 0) return;
      try {
        const response = await this.$store.dispatch("project/LOAD_FIELD_LIST", {
          projectId: parseInt(this.projectId),
          page: 1,
          perPage: 200,
        });
        const data = (response && response.data) || [];
        this.fieldNames = data.map((field) => field.field_name).filter(Boolean);
      } catch (e) {
        this.fieldNames = [];
      }
      if (this.fieldNames.length === 0) {
        this.fieldNames = ["인천공장", "포항공장", "당진공장"];
      }
    },
    issuedOf(row) {
      return this.issuedByField[row.사업장명] || 0;
    },
    remainOf(row) {
      const cap = parseInt(row.점수);
      if (isNaN(cap)) return "-";
      return cap - this.issuedOf(row);
    },
    remainClass(row) {
      const remain = this.remainOf(row);
      return typeof remain === "number" && remain < 0 ? "cap-over" : "";
    },
    async loadCap() {
      this.loading = true;
      try {
        // Cap 설정(1231)과 현재 발부 점수(1228 승인완료 건)를 함께 조회
        const [response, issuedByField] = await Promise.all([
          this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId: parseInt(this.projectId),
            boardId: CAP_BOARD_ID,
            page: 1,
            perPage: 1,
            conditions: [{ key: "body.연도", operator: "=", value: `${this.year}` }],
            // LOAD_POST_LIST 5초 메모리 캐시 우회용 (저장 직후 재조회 대비)
            cacheBuster: Date.now(),
          }),
          loadIssuedScoreByField(this.$store, this.projectId, this.year, { boardId: parseInt(this.boardId) || undefined }).catch(() => ({})),
        ]);
        this.issuedByField = issuedByField;
        this.capPost = response.data && response.data.length > 0 ? response.data[0] : null;

        const savedList = [];
        if (this.capPost) {
          try {
            const body = JSON.parse(this.capPost.post_content).body || {};
            (body.capList || []).forEach((row) => savedList.push({ 사업장명: row.사업장명, 점수: row.점수 }));
          } catch (e) {
            /* ignore */
          }
        }

        // 사업장 목록 기준으로 행 구성 + 저장된 값 병합 (목록에 없는 저장 행도 유지)
        const merged = this.fieldNames.map((name) => {
          const saved = savedList.find((row) => row.사업장명 === name);
          return { 사업장명: name, 점수: saved ? saved.점수 : "" };
        });
        savedList.forEach((row) => {
          if (!merged.find((m) => m.사업장명 === row.사업장명)) {
            merged.push({ 사업장명: row.사업장명, 점수: row.점수 });
          }
        });
        this.capList = merged;
      } catch (e) {
        this.capList = this.fieldNames.map((name) => ({ 사업장명: name, 점수: "" }));
        this.capPost = null;
        this.issuedByField = {};
      }
      this.loading = false;
    },
    async saveCap() {
      if (this.saving) return;
      this.saving = true;
      try {
        const content = JSON.stringify({
          header: {
            year: this.year,
            month: 1,
            date: `${this.year}-01-01`,
            full: `${this.year}-01-01`,
          },
          body: {
            연도: this.year,
            capList: this.capList.map((row) => ({ 사업장명: row.사업장명, 점수: row.점수 })),
          },
        });
        const base = {
          projectId: parseInt(this.projectId),
          boardId: CAP_BOARD_ID,
          title: `${this.year}년 사업장별 Cap 점수`,
          content,
        };
        if (this.capPost) {
          await this.$store.dispatch("board/UPDATE_POST", { ...base, postId: this.capPost.id });
        } else {
          await this.$store.dispatch("board/CREATE_POST", base);
        }
        this.$vs.notify({ title: "저장", text: "사업장별 Cap 점수가 저장되었습니다.", color: "success" });
        this.showPopup = false;
      } catch (e) {
        this.$vs.notify({ title: "오류", text: "저장에 실패했습니다.", color: "danger" });
      }
      this.saving = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.incentive-cap-summary {
  display: inline-block;
}
.cap-popup {
  padding: 4px;
}
.cap-popup-year {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.cap-popup-label {
  font-weight: 600;
}
.cap-popup-desc {
  font-size: 12px;
  color: #888;
}
.cap-popup-loading {
  padding: 20px 0;
}
.cap-popup-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #e0e0e0;
    padding: 8px 10px;
    font-size: 13px;
  }

  th {
    background: #f7f8fa;
    font-weight: 600;
  }

  td.cap-over {
    color: #e42728;
    font-weight: 600;
  }
}
.cap-popup-note {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}
.cap-popup-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
</style>
